import { useCallback, useContext } from 'react';

import { format } from '@dynatrace-sdk/units';

import type {
  AnyNumericFormatter,
  CategoricalFormatter,
} from '../../core/types/formatter.js';
import { isFormatterOptions } from '../../core/types/formatter.js';
import { XYChartDataContext } from '../context/XYChartData.context.js';
import { isCategorical } from '../types/guards.js';
import type { XYChartAxisInternal } from '../types/xy-chart-internals.js';
import type {
  XYChartXAxisPosition,
  XYChartYAxisPosition,
} from '../types/xy-chart.js';
import { useVisibleAxesConfig } from './useVisibleAxesConfig.js';

export type XYChartAxisPosition =
  | XYChartXAxisPosition
  | XYChartYAxisPosition
  | 'top'
  | 'bottom'
  | 'left'
  | 'right';

export type FormatCategory = (
  axis: XYChartAxisInternal | XYChartAxisPosition | undefined,
  raw: string | undefined,
) => string | undefined;

/**
 * Returns a stable function that formats a raw category string using the
 * categorical axis formatter. The axis can be addressed by position (`'top'`)
 * or by passing the internal axis config directly. Falls back to the raw value
 * when the axis isn't categorical, has no formatter, or the value is missing.
 *
 * @internal
 */
export const useFormatCategory = (): FormatCategory => {
  const config = useVisibleAxesConfig();
  const context = useContext(XYChartDataContext);

  if (context === undefined) {
    throw new Error(
      'useFormatCategory must be used within a XYChartDataContext',
    );
  }

  const { axisIdToCategoryDatum } = context;

  return useCallback<FormatCategory>(
    (axisOrPosition, raw) => {
      if (typeof raw !== 'string') {
        return raw;
      }

      const axis =
        typeof axisOrPosition === 'string'
          ? config[axisOrPosition as keyof typeof config]
          : axisOrPosition;

      if (!axis || !isCategorical(axis.type)) {
        return raw;
      }

      const formatter = axis.formatter;
      if (!formatter) {
        return raw;
      }

      const numericFormatter = formatter as AnyNumericFormatter;
      if (isFormatterOptions(numericFormatter)) {
        return format(Number(raw), numericFormatter);
      }

      const datum = axisIdToCategoryDatum.get(axis.id)?.get(raw) ?? {};
      return (formatter as CategoricalFormatter)(raw, datum);
    },
    [config, axisIdToCategoryDatum],
  );
};
